import { useMemo, useState } from "react";
import { Check, Pencil, Plus, Trash2, Undo2, X } from "lucide-react";
import { clsx } from "clsx";
import { DatePicker } from "@/components/ui/DatePicker";
import { SectionCard } from "../components/ui";
import { fmtDate } from "../utils";
import { useMyTaskMutation, useMyTasks } from "./hooks";
import { DeadlineCell, ItemCell, TABLE_CLS, Td, Th, TRow, type Tone } from "./tableUi";
import type { PersonalTask } from "./types";

const PRIORITY_PL: Record<PersonalTask["priority"], string> = {
  urgent: "Pilne", high: "Wysokie", normal: "Normalne", low: "Niskie",
};
const PRIORITY_DOT: Record<PersonalTask["priority"], string> = {
  urgent: "#dc2626", high: "#ea580c", normal: "#9ca3af", low: "#d1d5db",
};
const PRIORITIES = ["urgent", "high", "normal", "low"] as const;

/** Ile dni przed terminem pozycja robi się „blisko terminu" — ten sam próg,
 * którego dla pozycji procesowych używa backend (warn_days). */
const WARN_DAYS = 3;

const BTN =
  "inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-4 py-1.5 bg-[var(--color-primary)] text-white hover:opacity-90 transition-opacity disabled:opacity-50";
const ICON_BTN =
  "p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors disabled:opacity-40";

/** Dni do terminu liczone po dacie kalendarzowej (bez godzin) — „dziś" to 0. */
function daysLeft(due: string | null): number | null {
  if (!due) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(`${due}T00:00:00`);
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

function toneOf(t: PersonalTask): Tone {
  if (t.is_done) return "idle";
  const left = daysLeft(t.due_date);
  if (left == null) return "ok";
  return left < 0 ? "late" : left <= WARN_DAYS ? "warn" : "ok";
}

function DueCell({ t }: { t: PersonalTask }) {
  const date = t.due_date ? fmtDate(t.due_date) : null;
  if (t.is_done) return <DeadlineCell date={date} tone="idle" state="zrobione" />;
  const left = daysLeft(t.due_date);
  if (left == null) return <DeadlineCell date={date} tone="ok" state="bez terminu" />;
  if (left < 0) return <DeadlineCell date={date} tone="late" state={`po terminie o ${-left} dn.`} />;
  return (
    <DeadlineCell
      date={date}
      tone={left <= WARN_DAYS ? "warn" : "ok"}
      state={left === 0 ? "dziś" : `zostało ${left} dn.`}
    />
  );
}

type Draft = { title: string; due_date: string; priority: PersonalTask["priority"]; note: string };

const EMPTY: Draft = { title: "", due_date: "", priority: "normal", note: "" };

/** Formularz zadania — ten sam dla dodawania i edycji (różni się tylko etykietą
 * przycisku), żeby oba widoki miały identyczne pola i walidację. */
function TaskForm({ value, onChange, onSubmit, onCancel, submitLabel, busy }: {
  value: Draft;
  onChange: (d: Draft) => void;
  onSubmit: () => void;
  onCancel?: () => void;
  submitLabel: string;
  busy: boolean;
}) {
  const valid = value.title.trim().length > 0;
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(e) => { e.preventDefault(); if (valid && !busy) onSubmit(); }}
    >
      <input
        className="input-base flex-1 min-w-[16rem] text-sm"
        placeholder="Co masz do zrobienia?"
        maxLength={255}
        value={value.title}
        onChange={(e) => onChange({ ...value, title: e.target.value })}
      />
      <DatePicker
        className="w-40 text-xs"
        aria-label="Termin (opcjonalny)"
        placeholder="Termin (opcjonalny)"
        value={value.due_date}
        onChange={(v) => onChange({ ...value, due_date: v })}
      />
      <select
        className="input-base w-32 text-xs"
        title="Priorytet"
        value={value.priority}
        onChange={(e) => onChange({ ...value, priority: e.target.value as PersonalTask["priority"] })}
      >
        {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_PL[p]}</option>)}
      </select>
      <input
        className="input-base flex-1 min-w-[12rem] text-xs"
        placeholder="Notatka (opcjonalnie)"
        value={value.note}
        onChange={(e) => onChange({ ...value, note: e.target.value })}
      />
      <button type="submit" className={BTN} disabled={!valid || busy}>
        <Plus size={13} /> {submitLabel}
      </button>
      {onCancel && (
        <button type="button" className={ICON_BTN} title="Anuluj" onClick={onCancel}>
          <X size={15} />
        </button>
      )}
    </form>
  );
}

/** Własne zadania na „Moje TODO": pozycje spoza procesu projektów ZE — nie
 * wynikają z kamieni/spraw/działek, więc nikt ich nie policzy za użytkownika.
 * Lista jest prywatna (backend zawęża ją do właściciela), więc nie ma tu kolumn
 * „projekt" ani „kto" — zawsze chodzi o zalogowaną osobę. */
export function PersonalTasksCard() {
  const { data, isLoading } = useMyTasks();
  const { create, update, remove } = useMyTaskMutation();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(EMPTY);
  const [showDone, setShowDone] = useState(false);

  const tasks = useMemo(() => data ?? [], [data]);
  const open = tasks.filter((t) => !t.is_done);
  const done = tasks.filter((t) => t.is_done);
  const overdue = open.filter((t) => (daysLeft(t.due_date) ?? 0) < 0).length;
  const visible = showDone ? tasks : open;

  const submitNew = () => {
    create.mutate(
      {
        title: draft.title.trim(),
        note: draft.note.trim(),
        due_date: draft.due_date || null,
        priority: draft.priority,
      },
      { onSuccess: () => setDraft(EMPTY) },
    );
  };

  const startEdit = (t: PersonalTask) => {
    setEditingId(t.id);
    setEditDraft({ title: t.title, due_date: t.due_date ?? "", priority: t.priority, note: t.note });
  };

  const submitEdit = () => {
    if (!editingId) return;
    update.mutate(
      {
        id: editingId,
        title: editDraft.title.trim(),
        note: editDraft.note.trim(),
        due_date: editDraft.due_date || null,
        priority: editDraft.priority,
      },
      { onSuccess: () => setEditingId(null) },
    );
  };

  const busy = create.isPending || update.isPending || remove.isPending;

  return (
    <div className="mt-4">
      <SectionCard
        title="Moje zadania własne — poza projektami"
        action={
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
              {open.length} otwarte
              {overdue > 0 && <span className="text-red-600 font-bold"> · {overdue} po terminie</span>}
            </span>
            {done.length > 0 && (
              <button
                type="button"
                onClick={() => setShowDone((v) => !v)}
                className="font-mono text-[10px] uppercase tracking-wide text-[var(--color-primary)]"
              >
                {showDone ? "ukryj ukończone" : `pokaż ukończone (${done.length})`}
              </button>
            )}
          </div>
        }
      >
        <TaskForm
          value={draft}
          onChange={setDraft}
          onSubmit={submitNew}
          submitLabel="Dodaj"
          busy={create.isPending}
        />
        {(create.isError || update.isError || remove.isError) && (
          <div className="text-xs text-red-600 mt-2">
            Nie udało się zapisać zmiany — spróbuj ponownie.
          </div>
        )}

        {isLoading ? (
          <div className="text-sm text-[var(--color-text-muted)] mt-3">Ładowanie…</div>
        ) : visible.length === 0 ? (
          <div className="text-sm text-[var(--color-text-muted)] mt-3">
            Brak własnych zadań — dodaj pierwsze powyżej. To pozycje, których nie ma w procesie
            projektów (telefon, dokument do odebrania, cokolwiek), widoczne tylko dla Ciebie.
          </div>
        ) : (
          <div className="border-t border-[var(--color-border)] mt-3">
            <table className={TABLE_CLS}>
              <colgroup>
                <col style={{ width: "6%" }} />
                <col />
                <col style={{ width: "16%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "10%" }} />
              </colgroup>
              <thead>
                <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                  <Th>Zrobione</Th>
                  <Th>Zadanie</Th>
                  <Th>Termin</Th>
                  <Th>Priorytet</Th>
                  <Th align="right">Akcje</Th>
                </tr>
              </thead>
              <tbody>
                {visible.map((t) => (
                  editingId === t.id ? (
                    <tr key={t.id} className="border-b border-[var(--color-border-muted)]">
                      <td colSpan={5} className="py-2 px-2 bg-[var(--color-bg-elevated)]">
                        <TaskForm
                          value={editDraft}
                          onChange={setEditDraft}
                          onSubmit={submitEdit}
                          onCancel={() => setEditingId(null)}
                          submitLabel="Zapisz"
                          busy={update.isPending}
                        />
                      </td>
                    </tr>
                  ) : (
                    <TRow key={t.id} tone={toneOf(t)}>
                      <Td>
                        <button
                          type="button"
                          disabled={busy}
                          title={t.is_done ? "Cofnij do zrobienia" : "Oznacz jako zrobione"}
                          onClick={() => update.mutate({ id: t.id, is_done: !t.is_done })}
                          className={clsx(
                            "w-5 h-5 border flex items-center justify-center transition-colors disabled:opacity-40",
                            t.is_done
                              ? "border-emerald-600 text-emerald-600"
                              : "border-[var(--color-border)] text-transparent hover:border-[var(--color-primary)] hover:text-[var(--color-text-muted)]",
                          )}
                        >
                          <Check size={12} />
                        </button>
                      </Td>
                      <Td className={clsx(t.is_done && "opacity-60 line-through")}>
                        <ItemCell kind="własne" label={t.title} />
                        {t.note && (
                          <div className="text-xs text-[var(--color-text-secondary)] mt-0.5 break-words">
                            {t.note}
                          </div>
                        )}
                      </Td>
                      <Td><DueCell t={t} /></Td>
                      <Td>
                        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-secondary)]">
                          <span className="w-1.5 h-1.5 shrink-0" style={{ background: PRIORITY_DOT[t.priority] }} />
                          {PRIORITY_PL[t.priority]}
                        </span>
                      </Td>
                      <Td align="right">
                        <div className="inline-flex items-center gap-1">
                          {t.is_done && (
                            <button
                              type="button"
                              className={ICON_BTN}
                              title="Cofnij do zrobienia"
                              disabled={busy}
                              onClick={() => update.mutate({ id: t.id, is_done: false })}
                            >
                              <Undo2 size={14} />
                            </button>
                          )}
                          <button
                            type="button"
                            className={ICON_BTN}
                            title="Edytuj"
                            disabled={busy}
                            onClick={() => startEdit(t)}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            className={clsx(ICON_BTN, "hover:text-red-600")}
                            title="Usuń"
                            disabled={busy}
                            onClick={() => {
                              if (window.confirm(`Usunąć zadanie „${t.title}"?`)) remove.mutate(t.id);
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </Td>
                    </TRow>
                  )
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[10px] font-mono text-[var(--color-text-muted)] mt-2">
          Zadania własne widzisz tylko Ty — nie trafiają do TODO projektów ani do widoków nadzoru.
        </p>
      </SectionCard>
    </div>
  );
}
